/**
 * VoyageHistoryView.js
 *
 * Full-screen view of all voyage history with search/filter
 * FT-160: Changed from card layout to table/list layout matching Crew List pattern
 */

import React, { useState, useMemo } from 'react';
import { Ship, Search, Clock, ChevronRight, CheckCircle, XCircle, AlertCircle, Download, Calendar, X } from 'lucide-react';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
// FT-195: Batch voyage PDF export
import { exportVoyageBatchPDF, DATE_RANGE_PRESETS, getDateRangeFromPreset } from '../../../../utils/pdf/voyageExport';
import { portLabel } from '../../../../utils/voyage/portLabel';

const VoyageHistoryView = ({ voyages, vesselName, onClose, onSelectVoyage }) => {
  // FT-357: register onClose with the shell-level MobileBottomBar.
  useSetBackHandler(onClose);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterMonth, setFilterMonth] = useState('all');

  // FT-195: Batch export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Get unique months from voyages
  const availableMonths = useMemo(() => {
    const months = new Set();
    voyages.forEach(v => {
      if (v.departureTime) {
        const date = new Date(v.departureTime);
        months.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
      }
    });
    return Array.from(months).sort().reverse();
  }, [voyages]);

  // Filter voyages
  const filteredVoyages = useMemo(() => {
    return voyages.filter(voyage => {
      // Search filter
      const searchMatch = searchTerm === '' ||
        portLabel(voyage.from, '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        portLabel(voyage.to, '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (voyage.voyageNumber || '').toLowerCase().includes(searchTerm.toLowerCase());

      // Month filter
      let monthMatch = true;
      if (filterMonth !== 'all' && voyage.departureTime) {
        const date = new Date(voyage.departureTime);
        const voyageMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthMatch = voyageMonth === filterMonth;
      }

      return searchMatch && monthMatch;
    });
  }, [voyages, searchTerm, filterMonth]);

  // Calculate duration helper
  const getDuration = (voyage) => {
    if (!voyage.departureTime || !voyage.arrivalTime) return '-';
    const start = new Date(voyage.departureTime);
    const end = new Date(voyage.arrivalTime);
    const hours = Math.round((end - start) / (1000 * 60 * 60) * 10) / 10;
    return `${hours}h`;
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format month for display
  const formatMonth = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  // Get check counts from unified log
  const getCheckCounts = (voyage) => {
    const unifiedLog = voyage.unifiedLog || [];
    return unifiedLog.filter(e => e.type === 'check').length;
  };

  // Get status info
  const getStatusInfo = (voyage) => {
    if (voyage.status === 'completed') {
      return { text: 'Completed', color: 'bg-green-50 text-green-700 ring-green-600/20', icon: CheckCircle };
    }
    if (voyage.status === 'cancelled') {
      return { text: 'Cancelled', color: 'bg-red-50 text-red-700 ring-red-600/20', icon: XCircle };
    }
    if (voyage.status === 'in_progress') {
      return { text: 'In Progress', color: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20', icon: Clock };
    }
    return { text: voyage.status || 'Unknown', color: 'bg-gray-50 text-gray-700 ring-gray-600/20', icon: AlertCircle };
  };

  // FT-195: Handle batch export
  const handleBatchExport = async () => {
    setIsExporting(true);
    try {
      let startDate, endDate;

      if (selectedPreset === 'custom') {
        startDate = customStartDate ? new Date(customStartDate) : null;
        endDate = customEndDate ? new Date(customEndDate) : null;
      } else {
        const range = getDateRangeFromPreset(selectedPreset);
        startDate = range.startDate;
        endDate = range.endDate;
      }

      // Filter voyages for export (only completed ones within date range)
      const voyagesToExport = voyages.filter(v => {
        if (v.status !== 'completed') return false;
        if (!v.departureTime) return false;
        const voyageDate = new Date(v.departureTime);
        if (startDate && voyageDate < startDate) return false;
        if (endDate && voyageDate > endDate) return false;
        return true;
      });

      if (voyagesToExport.length === 0) {
        alert('No completed voyages found in the selected date range.');
        return;
      }

      await exportVoyageBatchPDF(voyagesToExport, vesselName || 'Unknown Vessel', { startDate, endDate });
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // FT-195: Get count of exportable voyages for preview
  const getExportableCount = () => {
    let startDate, endDate;
    if (selectedPreset === 'custom') {
      startDate = customStartDate ? new Date(customStartDate) : null;
      endDate = customEndDate ? new Date(customEndDate) : null;
    } else {
      const range = getDateRangeFromPreset(selectedPreset);
      startDate = range.startDate;
      endDate = range.endDate;
    }

    return voyages.filter(v => {
      if (v.status !== 'completed') return false;
      if (!v.departureTime) return false;
      const voyageDate = new Date(v.departureTime);
      if (startDate && voyageDate < startDate) return false;
      if (endDate && voyageDate > endDate) return false;
      return true;
    }).length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center gap-4">
            {/* FT-357: header BackButton removed — bottom bar owns Back. */}
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-gray-900">Voyage History</h1>
              <p className="text-sm text-gray-500">{voyages.length} voyages total</p>
            </div>
            {/* FT-195: Export Voyages button */}
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>

          {/* Search */}
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search voyages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          {/* Month Filter */}
          <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setFilterMonth('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap ${
                filterMonth === 'all'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Time
            </button>
            {availableMonths.slice(0, 6).map(month => (
              <button
                key={month}
                onClick={() => setFilterMonth(month)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap ${
                  filterMonth === month
                    ? 'bg-cyan-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {formatMonth(month)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Voyage Table */}
      <div className="p-4">
        {filteredVoyages.length > 0 ? (
          <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Voyage #
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Route
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Checks
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="relative px-4 py-3">
                      <span className="sr-only">View</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredVoyages.map((voyage) => {
                    const statusInfo = getStatusInfo(voyage);
                    const StatusIcon = statusInfo.icon;
                    const checkCount = getCheckCounts(voyage);

                    return (
                      <tr
                        key={voyage.id}
                        onClick={() => onSelectVoyage(voyage)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900">
                          {voyage.voyageNumber || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[120px]">{portLabel(voyage.from)}</span>
                            <span className="text-gray-400">→</span>
                            <span className="truncate max-w-[120px]">{portLabel(voyage.to)}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                          {formatDate(voyage.departureTime)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                            {getDuration(voyage)}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                          {checkCount > 0 ? (
                            <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600">
                              {checkCount} checks
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm">
                          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${statusInfo.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {statusInfo.text}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-right text-sm">
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile List */}
            <div className="md:hidden divide-y divide-gray-200 bg-white">
              {filteredVoyages.map((voyage) => {
                const statusInfo = getStatusInfo(voyage);
                const StatusIcon = statusInfo.icon;
                const checkCount = getCheckCounts(voyage);

                return (
                  <button
                    key={voyage.id}
                    onClick={() => onSelectVoyage(voyage)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
                  >
                    <div className="p-2 bg-cyan-50 rounded-lg shrink-0">
                      <Ship className="h-4 w-4 text-cyan-600" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 text-sm">
                          {voyage.voyageNumber || 'Voyage'}
                        </span>
                        <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${statusInfo.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          {statusInfo.text}
                        </span>
                      </div>

                      <div className="text-sm text-gray-600 mt-0.5 truncate">
                        {portLabel(voyage.from)} → {portLabel(voyage.to)}
                      </div>

                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{formatDate(voyage.departureTime)}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {getDuration(voyage)}
                        </span>
                        {checkCount > 0 && (
                          <span>{checkCount} checks</span>
                        )}
                      </div>
                    </div>

                    <ChevronRight className="h-5 w-5 text-gray-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow ring-1 ring-black ring-opacity-5">
            <Ship className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No voyages found</p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-sm text-cyan-600 mt-2"
              >
                Clear search
              </button>
            )}
          </div>
        )}
      </div>

      {/* FT-195: Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Export Voyages</h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Select a date range to export completed voyages as a PDF report.
              </p>

              {/* Date Range Presets */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Date Range</label>
                <div className="grid grid-cols-2 gap-2">
                  {DATE_RANGE_PRESETS.map(preset => (
                    <button
                      key={preset.value}
                      onClick={() => setSelectedPreset(preset.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedPreset === preset.value
                          ? 'bg-cyan-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Date Range */}
              {selectedPreset === 'custom' && (
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Preview Count */}
              <div className="flex items-center gap-2 p-3 bg-cyan-50 rounded-lg">
                <Calendar className="h-5 w-5 text-cyan-600" />
                <span className="text-sm text-cyan-800">
                  <strong>{getExportableCount()}</strong> completed voyage{getExportableCount() !== 1 ? 's' : ''} will be exported
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowExportModal(false)}
                className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBatchExport}
                disabled={isExporting || getExportableCount() === 0}
                className="flex-1 py-2.5 px-4 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isExporting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Export PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoyageHistoryView;
